import { useState, useMemo } from "react";
import { Target, MapPin, ChevronDown, ChevronRight, Paperclip, AlertTriangle, Flame } from "lucide-react";
import { progressoDoDia, contatos, contatosNaSemana, contatosNoDia, porRegiao, porPessoa, diasEmDebito, metaDe, resumoAtraso } from "../lib/metas";
import { mensagem, sequencia, CORES_TOM } from "../lib/incentivo";
import { somarDias } from "../lib/abordagem";
import { fmtShort } from "../utils";

// ═══════════════════════════════════════════════════════════════
// PAINEL DE META — o dia de quem está trabalhando
// ═══════════════════════════════════════════════════════════════
//
// Fica no topo da Rede, sempre visível. Para o comercial mostra o próprio dia;
// para gerente e administrador, o time inteiro e onde a rede está sendo
// batida — primeiro a região, depois a cidade dentro dela.
//
// Contamos contato COM PROVA à parte, sempre. Meta que aceita registro sem
// print ensina a inventar registro, e aí o número sobe enquanto a rede não
// cresce.

export default function PainelMeta({ partners = [], metas = [], userId, role = "comercial", hoje }) {
  const [aberto, setAberto] = useState(false);
  const gerencial = role === "gerente" || role === "admin";

  const prog = useMemo(() => {
    const base = progressoDoDia(partners, userId, metas, hoje);
    if (!gerencial) return base;
    // Admin/gerente: o número do topo é o TIME inteiro — todos os contatos e
    // exports do dia, não só os que o próprio admin registrou. Sem isto, o
    // painel de quem não prospecta pessoalmente fica sempre zerado, mesmo com o
    // time inteiro lançando conversa.
    const lista = contatos(partners, {});
    const doDia = contatosNoDia(lista, hoje);
    return {
      ...base, time: true,
      meta: 0, faltam: 0, bateu: false, percentual: 0, metaSemana: 0, individual: false,
      feitos: doDia.length,
      comProva: doDia.filter((c) => c.comprovado).length,
      semana: contatosNaSemana(lista, hoje).length,
    };
  }, [partners, metas, userId, hoje, gerencial]);
  const debitos = useMemo(() => diasEmDebito(partners, userId, metas, hoje), [partners, metas, userId, hoje]);
  const regioes = useMemo(
    () => porRegiao(contatosNaSemana(contatos(partners, gerencial ? {} : { userId }), hoje)),
    [partners, gerencial, userId, hoje]
  );
  const pessoas = useMemo(() => (gerencial ? porPessoa(partners, metas, hoje) : []), [partners, metas, gerencial, hoje]);

  // Divergência entre o dia da conversa e o dia do lançamento. Só a gerência
  // vê: a meta continua contando pelo lançamento, e isto é contexto para
  // interpretar o número — não é cobrança em cima de quem registrou.
  const atraso = useMemo(
    () => (gerencial ? resumoAtraso(contatosNoDia(contatos(partners), hoje)) : { total: 0 }),
    [partners, gerencial, hoje],
  );

  // Meta zero não é meta batida nem meta furada: é ausência de meta. Mostrar
  // "0 de 0" faz o painel parecer quebrado justamente para quem ainda não foi
  // incluído na régua.
  const semMeta = !prog.meta;
  const cor = semMeta ? "#64748b" : prog.bateu ? "#059669" : prog.percentual >= 50 ? "#d97706" : "#dc2626";
  const msg = useMemo(() => mensagem({ partners, metas, userId, hoje, progresso: prog }), [partners, metas, userId, hoje, prog]);
  const seq = useMemo(() => sequencia(partners, userId, metas, hoje), [partners, userId, metas, hoje]);

  // Os últimos 7 dias em barrinhas: o comercial acompanha o próprio ritmo sem
  // depender de alguém montar relatório para ele.
  const semana7 = useMemo(() => {
    const meta = metaDe(metas, userId).contatosDia || 0;
    const lista = contatos(partners, gerencial ? {} : { userId });
    return Array.from({ length: 7 }, (_, k) => {
      const dia = somarDias(hoje, -(6 - k));
      const feitos = contatosNoDia(lista, dia).length;
      const fds = [0, 6].includes(new Date(`${dia}T12:00:00Z`).getUTCDay());
      return { dia, feitos, meta, bateu: meta > 0 && feitos >= meta, fds, hoje: dia === hoje };
    });
  }, [partners, metas, userId, hoje, gerencial]);

  return (
    <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", borderLeft: `4px solid ${cor}`, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Target size={16} color={cor} />
        <div style={{ minWidth: 150 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
            {prog.time
              ? `${prog.feitos} ${prog.feitos === 1 ? "contato do time" : "contatos do time"} hoje`
              : semMeta
                ? `${prog.feitos} ${prog.feitos === 1 ? "contato" : "contatos"} hoje`
                : `${prog.feitos} de ${prog.meta} ${prog.meta === 1 ? "contato" : "contatos"} hoje`}
          </div>
          <div style={{ fontSize: 10.5, color: "#64748b" }}>
            {prog.time
              ? "Todos os contatos e exports do time hoje. Cada conversa lançada conta."
              : semMeta
              ? "Sem meta diária definida — a gerência define na aba Equipe."
              : prog.bateu
                ? "Meta do dia batida."
                : prog.faltam === prog.meta
                  ? "Nenhum contato registrado ainda hoje."
                  : `Faltam ${prog.faltam} para bater a meta.`}
            {prog.individual && !semMeta && " (meta individual)"}
          </div>
        </div>

        {!semMeta && (
          <div style={{ flex: 1, minWidth: 140, height: 8, borderRadius: 5, background: "#f1f5f9", overflow: "hidden" }}>
            <div style={{ width: `${prog.percentual}%`, height: "100%", background: cor, transition: "width .3s" }} />
          </div>
        )}

        <div style={{ display: "flex", gap: 14, fontSize: 10.5, color: "#475569" }}>
          <span title="Contatos com print anexado">
            <Paperclip size={10} style={{ verticalAlign: "-1px" }} /> {prog.comProva}/{prog.feitos} com prova
          </span>
          <span>Semana: <strong style={{ color: "#0f172a" }}>{prog.semana}</strong>{prog.metaSemana ? `/${prog.metaSemana}` : ""}</span>
        </div>

        {gerencial && atraso.total > 0 && (
          <span
            title={`Conversas de outros dias lançadas hoje — a mais antiga tem ${atraso.pior} dias. A meta conta pelo lançamento; isto é só para leitura do número.`}
            style={{ fontSize: 9.5, fontWeight: 700, color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", padding: "3px 8px", borderRadius: 10, whiteSpace: "nowrap" }}
          >
            {atraso.total} de outro dia · até {atraso.pior}d
          </span>
        )}

        <button
          onClick={() => setAberto((v) => !v)}
          style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 11, fontWeight: 600, cursor: "pointer" }}
        >
          {aberto ? <ChevronDown size={12} /> : <ChevronRight size={12} />} {gerencial ? "Time e regiões" : "Minhas regiões"}
        </button>
      </div>

      {/* A mensagem nasce de um fato da trilha, nunca de frase pronta: elogio
          genérico repetido todo dia vira ruído e soa falso em dia ruim. */}
      {msg && (
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 10, padding: "8px 11px", borderRadius: 8, background: CORES_TOM[msg.tom].fundo, border: `1px solid ${CORES_TOM[msg.tom].borda}`, fontSize: 11.5, color: CORES_TOM[msg.tom].texto, lineHeight: 1.4 }}>
          {seq >= 2 ? <Flame size={13} style={{ flexShrink: 0 }} /> : <Target size={13} style={{ flexShrink: 0 }} />}
          <span>{msg.texto}</span>
        </div>
      )}

      {/* Os 7 dias, para a pessoa acompanhar o próprio ritmo */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 4, marginTop: 12, height: 42 }}>
        {semana7.map((d) => (
          <div key={d.dia} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }} title={`${d.dia}: ${d.feitos} contatos`}>
            <div style={{ width: "100%", height: 28, display: "flex", alignItems: "flex-end", background: "#f8fafc", borderRadius: 4, overflow: "hidden" }}>
              <div style={{
                width: "100%",
                height: `${d.meta ? Math.min(100, Math.round((d.feitos / d.meta) * 100)) : (d.feitos ? 100 : 0)}%`,
                background: d.bateu ? "#059669" : d.feitos ? "#f59e0b" : "#e2e8f0",
                transition: "height .3s",
              }} />
            </div>
            <span style={{ fontSize: 8.5, color: d.hoje ? "#0f172a" : "#cbd5e1", fontWeight: d.hoje ? 700 : 500 }}>
              {d.fds ? "·" : d.dia.slice(8, 10)}
            </span>
          </div>
        ))}
      </div>

      {/* Dias fechados sem bater a meta — olha só para trás, nunca para hoje,
          que ainda está acontecendo. */}
      {debitos.length > 0 && (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginTop: 10, padding: "7px 10px", borderRadius: 7, background: "#fef2f2", border: "1px solid #fecaca", fontSize: 10.5, color: "#991b1b", lineHeight: 1.45 }}>
          <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>{debitos.length} {debitos.length === 1 ? "dia útil" : "dias úteis"} sem bater a meta</strong> nos últimos 7:{" "}
            {debitos.slice(0, 4).map((d) => `${fmtShort(d.dia)} (${d.feitos}/${d.meta})`).join(", ")}
            {debitos.length > 4 ? "…" : ""}
          </span>
        </div>
      )}

      {aberto && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #f1f5f9", display: "grid", gap: 14, gridTemplateColumns: gerencial ? "1fr 1fr" : "1fr" }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
              Contatos por região (7 dias)
            </div>
            {regioes.length === 0 && <div style={{ fontSize: 11, color: "#94a3b8" }}>Nenhum contato registrado nos últimos 7 dias.</div>}
            {regioes.map((r) => (
              <Regiao key={r.uf} r={r} />
            ))}
          </div>

          {gerencial && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 6 }}>
                Time — contatos na semana
              </div>
              {pessoas.length === 0 && <div style={{ fontSize: 11, color: "#94a3b8" }}>Ninguém registrou contato ainda.</div>}
              {pessoas.map((p) => (
                <div key={p.autor} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "5px 0", borderBottom: "1px solid #f8fafc", fontSize: 11.5 }}>
                  <span style={{ color: "#334155", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.autor || "sem autor"}</span>
                  <span style={{ color: "#64748b", whiteSpace: "nowrap" }}>
                    hoje <strong style={{ color: "#0f172a" }}>{p.hoje}</strong> · semana <strong style={{ color: "#0f172a" }}>{p.semana}</strong> ·{" "}
                    <span style={{ color: p.comProva === p.total ? "#059669" : "#b45309" }}>{p.comProva}/{p.total} c/ prova</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Regiao({ r }) {
  const [abre, setAbre] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid #f8fafc" }}>
      <div
        onClick={() => setAbre((v) => !v)}
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "5px 0", cursor: "pointer", fontSize: 11.5 }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 5, color: "#334155", fontWeight: 600 }}>
          {abre ? <ChevronDown size={11} color="#94a3b8" /> : <ChevronRight size={11} color="#94a3b8" />}
          <MapPin size={11} color="#7c3aed" /> {r.uf}
        </span>
        <span style={{ color: "#64748b", whiteSpace: "nowrap" }}>
          <strong style={{ color: "#0f172a" }}>{r.total}</strong> contatos · {r.empresas} {r.empresas === 1 ? "empresa" : "empresas"}
        </span>
      </div>
      {abre && (
        <div style={{ paddingLeft: 22, paddingBottom: 6 }}>
          {r.cidades.map((c) => (
            <div key={c.cidade} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b", padding: "2px 0" }}>
              <span>{c.cidade}</span><span>{c.total}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
